import { signIn, useSession } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { Dispatch, SetStateAction, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { TextField } from '@/components/Form/UI/TextField';
import LoginModal, {
  LoginModalActualForm,
} from '@/components/Modal/LoginModal';
import Button from '@/components/UI/Button';
import { EMAIL_REGEX } from '@/utils/regex';

import { AuthFormType, Login } from './Authform.type';
import OauthForm from './OauthForm';

interface LoginFormProps {
  onSuccessCallback: () => void;
  setSelectedForm: Dispatch<SetStateAction<AuthFormType>>;
  aditionalContent?: React.ReactNode;
}

export default function LoginForm({
  onSuccessCallback,
  setSelectedForm,
  aditionalContent,
}: LoginFormProps) {
  const { status } = useSession();
  const tErrors = useTranslations('Errors.Form');
  const t = useTranslations('Components.Form.LoginForm');

  const [isLoading, setIsLoading] = useState(false);

  const [bypassResetEmailSending, setBypassResetEmailSending] = useState(false);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalDefaultForm, setLoginModalDefaultForm] =
    useState<LoginModalActualForm>('login');

  const {
    register,
    handleSubmit,
    control,
    getValues,
    formState: { isValid, errors },
  } = useForm<Login>({ mode: 'onBlur' });

  const onSubmit = async (formdata: Login) => {
    setIsLoading(true);
    signIn('credentials', {
      type: 'login',
      redirect: false,
      username: formdata.username,
      password: formdata.password,
    })
      .then(async (response) => {
        if (response?.error) {
          const error = JSON.parse(response.error);
          if (error) {
            toast.error(error.message);
          } else {
            toast.error(tErrors('invalid_user_credentials'));
          }
        } else {
          onSuccessCallback?.();
        }
      })
      .catch(() => {
        toast.error(tErrors('errorOccurred'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <form className='mb-10 space-y-4' onSubmit={handleSubmit(onSubmit)}>
        {aditionalContent}
        <div className='space-y-2'>
          <OauthForm />
          <div className='mx-auto  w-full sm:max-w-md'>
            <Button
              variant='basic'
              onClick={() => {
                document.body.scrollIntoView({ behavior: 'smooth' });
                setSelectedForm('register');
              }}
              width='full'
              type='button'
            >
              <span className='font-bold'>{t('dontHaveAccount')}</span>
            </Button>
          </div>
        </div>

        <p className='text-lg font-bold text-white-filled-default'>
          {t('connect')}
        </p>
        <TextField
          {...register('username', {
            required: true,
            pattern: {
              value: EMAIL_REGEX,
              message: tErrors('invalidEmail'),
            },
          })}
          label={t('email')}
          placeholder=''
          error={errors.username}
        />
        <TextField
          {...register('password', {
            required: true,
          })}
          type='password'
          label={t('password')}
          placeholder=''
        />
        <div className='flex flex-row-reverse'>
          <div className='max-w-max'>
            <Button
              variant='link'
              onClick={() => {
                document.body.scrollIntoView({ behavior: 'smooth' });
                setSelectedForm('forgot-password');
              }}
              width='full'
              type='button'
            >
              <span className='font-bold'>{t('forgotPassword')}</span>
            </Button>
          </div>
        </div>
        <div className='sm:mx-auto sm:max-w-md'>
          <Button
            width='full'
            padding='xl'
            disabled={!isValid || isLoading || status === 'loading'}
            type='submit'
            isLoading={isLoading}
          >
            {t('connect')}
          </Button>
        </div>
      </form>
      <div>
        {showLoginModal && (
          <LoginModal
            open={showLoginModal}
            setOpen={setShowLoginModal}
            email={getValues('username') as any}
            defaultForm={loginModalDefaultForm}
            onSuccessCallback={onSuccessCallback}
            bypassResetEmailSending={bypassResetEmailSending}
          />
        )}
      </div>
    </>
  );
}
